import React from 'react';
import { Chip, useTheme } from '@mui/material';
import { tokens } from '../../themeTokens';

/**
 * Enterprise-grade StatusBadge component
 * Displays status with consistent colors and styling
 *
 * @param {Object} props
 * @param {string} props.status - Status type: 'healthy', 'degraded', 'critical', 'unknown', 'success', 'warning', 'error', 'info'
 * @param {string} props.label - Custom label (defaults to status)
 * @param {string} props.size - Size: 'small' | 'medium'
 * @param {boolean} props.showIcon - Show status icon
 */
const StatusBadge = ({
  status = 'unknown',
  label,
  size = 'small',
  showIcon = true,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const getStatusConfig = () => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'success':
      case 'ok':
      case 'active':
        return {
          label: label || 'Healthy',
          color: colors.metrics.healthy,
          bgcolor: colors.status.successBg,
        };
      case 'degraded':
      case 'warning':
      case 'slow':
        return {
          label: label || 'Degraded',
          color: colors.metrics.degraded,
          bgcolor: colors.status.warningBg,
        };
      case 'critical':
      case 'error':
      case 'down':
      case 'failed':
        return {
          label: label || 'Critical',
          color: colors.metrics.critical,
          bgcolor: colors.status.errorBg,
        };
      case 'info':
      case 'pending':
        return {
          label: label || 'Info',
          color: colors.status.info,
          bgcolor: colors.status.infoBg,
        };
      case 'running':
      case 'online':
      case 'up':
        return {
          label: label || (status.toLowerCase() === 'running' ? 'Running' : 'Online'),
          color: colors.metrics.healthy,
          bgcolor: colors.status.successBg,
        };
      case 'offline':
        return {
          label: label || 'Offline',
          color: theme.palette.mode === 'dark' ? '#9ca3af' : '#6b7280',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(156,163,175,0.12)' : 'rgba(107,114,128,0.1)',
        };
      case 'unknown':
      case 'inactive':
      default:
        return {
          label: label || 'Unknown',
          color: colors.metrics.unknown,
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.08)',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Chip
      label={config.label}
      size={size}
      sx={{
        color: config.color,
        backgroundColor: config.bgcolor,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
        height: size === 'small' ? 20 : 24,
        '& .MuiChip-label': {
          px: 1,
        },
      }}
    />
  );
};

export default StatusBadge;
